'use client';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
   Box,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   useDisclosure,
   Checkbox,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerHeader,
   DrawerBody,
   useOutsideClick,
   Spinner,
   InputGroup,
   Thead,
   Th,
   Grid,
   GridItem,
   Input,
   Button,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Image,
   IconButton,
   Tooltip,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   useColorMode,
} from '@chakra-ui/react';
import Select from 'react-select';
import { LuRefreshCcw } from 'react-icons/lu';
import { ViewIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';

// Components
import Card from 'components/card/Card';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { columnsReportsSalesDetail } from '../../customDataTable/variables/columnsData';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getCompanies } from '../../../../redux/actions/company';
import { getReportPurchaseDetail } from '../../../../redux/actions/reports';

// Utils
import { getShareCompanyOptions } from 'utils/util';
import { REPORT_TYPE } from 'constants';

import SalesDetails from 'components/salesDetails/SalesDetails';
import { deletePurchaseInvoice, getSinglePurchaseWithPrevAndNext } from '../../../../redux/actions/purchaseInvoice';
import { deleteRecord } from 'utils/util';

// Assets
import nodata from 'assets/img/nfts/no data.svg';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { useGlobal } from 'contexts';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { IoFilterOutline } from 'react-icons/io5';
import {
   deletePurchaseReturnInvoice,
   getSinglePurchaseReturnWithPrevAndNext,
} from '../../../../redux/actions/purchaseReturnInvoice';
import { useUser } from 'contexts';
import Loader from 'views/admin/default/Loader';
import DeletePasswordModal from 'views/admin/sales/Pages/SalesInvoiceMaster/DeletePasswordModal';

// Constants
const REPORT_TYPE_OPTIONS = [
   { value: 'date', label: 'Date' },
   { value: 'party', label: 'Party' },
   { value: 'broker', label: 'Broker' },
];
const SEARCH_DEBOUNCE_DELAY = 300;

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Helper Components
const ColumnVisibilityModal = ({ isOpen, onClose, columns, columnVisibility, setColumnVisibility }) => (
   <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
         <ModalHeader>Show/Hide Columns</ModalHeader>
         <ModalCloseButton />
         <ModalBody>
            {columns.map((column) => (
               <Flex key={column.id} mb={2} alignItems="center">
                  <Checkbox
                     isChecked={!!columnVisibility[column.id]}
                     onChange={(e) => {
                        setColumnVisibility((prev) => ({
                           ...prev,
                           [column.id]: e.target.checked,
                        }));
                     }}
                     mr={2}
                  />
                  <Text>{column.header}</Text>
               </Flex>
            ))}
         </ModalBody>
         <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
               Close
            </Button>
         </ModalFooter>
      </ModalContent>
   </Modal>
);

const NoDataFound = ({ colSpan }) => (
   <Tr justifyContent="center" alignItems="center" width="100%" height="100%">
      <Td colSpan={colSpan}>
         <Grid justifyContent="center" alignItems="center" gap={9}>
            <img src={nodata} alt="No Data Found" width="200px" height="200px" />
            <GridItem justifySelf="center">
               <label>No purchase data found</label>
            </GridItem>
         </Grid>
      </Td>
   </Tr>
);

const PurchaseReturnSummary = () => {
   // Consolidated state
   const [state, setState] = useState({
      companyFilter: [],
      reportType: 'date',
      searchTerm: '',
      expandedItems: {},
      sorting: [],
      columnVisibility: {},
      isColumnSelectorOpen: false,
      isLoading: false,
      expanded: false,
   });

   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   // const [companyFilter, setCompanyFilter] = useState([]);

   // Refs
   const refs = {
      abortController: useRef(null),
      searchTimeout: useRef(null),
      wrapper: useRef(),
      fromDateInput: useRef(null),
   };

   const lastFetchParamsRef = useRef(null);
   const initialLoadRef = useRef(false);

   // Redux
   const dispatch = useDispatch();
   const company = useSelector((state) => state.company.list || []);
   const salesReport = useSelector((state) => state.reports?.purchaseDetail || []);
   const daybookData = useSelector((state) => state.daybook?.list || []);
   const dbName = localStorage.getItem('dbName');
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [datePartyWiseSalesRange, setPartyWiseSalesDateRange] = useState('');
   const viewData = useSelector((state) => state?.purchaseReturnInvoice?.purchaseReturnData);
   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData?.data[0];

      const pagination = viewData?.pagination;

      return { viewDetail: data, paginate: pagination };
   }, [viewData?.data, viewData?.pagination]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(null);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [dataId, setDataId] = useState(null);
   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
   const companyIds = useMemo(
      () =>
         state.companyFilter.length > 0
            ? state.companyFilter
            : localStorage.getItem('companyId')
            ? [parseInt(localStorage.getItem('companyId'))]
            : [],
      [state.companyFilter],
   );
   const { user } = useUser();
   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const purchaseInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchaseinvoice
      : null;
   const purchaseReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchasereturn
      : null;

   // Theme values
   const theme = {
      textColor: useColorModeValue('secondaryGray.900', 'white'),
      backgroundColor: useColorModeValue('white', '#101F4A'),
      borderColor: useColorModeValue('gray.200', 'whiteAlpha.100'),
      bg: useColorModeValue('#f2f7f2', '#1a254b'),
      icon: useColorModeValue('#000000', '#ffffff'),
   };
   const controlBg = useColorModeValue('#FFFFFF', '#2D3748');
   const controlBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const controlBorderHover = useColorModeValue('#CBD5E0', '#718096');
   const focusBoxShadow = useColorModeValue('0 0 0 1px #3182CE', '0 0 0 1px #63B3ED');
   const placeholderColor = useColorModeValue('#A0AEC0', '#718096');
   const optionBg = useColorModeValue('#FFFFFF', '#2D3748');
   const optionHoverBg = useColorModeValue('#EDF2F7', '#4A5568');
   const menuBg = useColorModeValue('#FFFFFF', '#2D3748');
   const menuBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const indicatorColor = useColorModeValue('#718096', '#A0AEC0');
   const indicatorHoverColor = useColorModeValue('#4A5568', '#CBD5E0');
   const { companyList } = useGlobal();
   const { userGlobalSettingsData } = useGlobal();
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });

   // Memoized values
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   // const columns = useMemo(() => {
   //    const columnHelper = createColumnHelper();
   //    return columnsReportsSalesDetail.map((column) =>
   //       columnHelper.accessor(column.id, {
   //          id: column.id,
   //          header: () => (
   //             <Text
   //                justifyContent="space-between"
   //                align="center"
   //                fontSize={{ sm: '10px', lg: '12px' }}
   //                color={theme.textColor}>
   //                {column.header}
   //             </Text>
   //          ),
   //          cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
   //       }),
   //    );
   // }, [theme.textColor]);

   const columns = useMemo(() => {
      const columnHelper = createColumnHelper();

      return columnsReportsSalesDetail.map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => {
               return (
                  <div
                     className={`
               flex justify-between items-center px-3 max-w-[150px]
               text-xs lg:text-sm 
               ${isDark ? 'text-gray-100' : 'text-gray90-0'}
               font-medium
             `}>
                     {column.header}
                  </div>
               );
            },
            cell: (info) => {
               const cellValue = info.getValue();
               const columnId = column.id;

               // Handle numeric columns with special formatting
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(columnId) && cellValue) {
                  return (
                     <div className={`flex items-center justify-end ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        ₹{Number(cellValue).toLocaleString()}
                     </div>
                  );
               }

               // Handle date columns
               if (columnId.includes('Date') && cellValue) {
                  return (
                     <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {new Date(cellValue).toLocaleDateString()}
                     </div>
                  );
               }

               // Handle numeric columns
               const isNumeric = [
                  'netAmount',
                  'dueDays',
                  'returnadjamount',
                  'otheradjamount',
                  'balanceamount',
                  'recDate',
                  'recBook',
                  'qty',
                  'quantity',
               ].includes(columnId);

               return (
                  <div
                     className={`
               flex items-center
               ${isNumeric ? 'justify-end' : 'justify-start'}
               ${isDark ? 'text-gray-100' : 'text-gray-900'}
             `}>
                     {cellValue}
                  </div>
               );
            },
         }),
      );
   }, [isDark]);

   // Table instance
   const table = useReactTable({
      data: salesReport,
      columns,
      state: {
         sorting: state.sorting,
         columnVisibility: state.columnVisibility,
      },
      onSortingChange: (sorting) => setState((prev) => ({ ...prev, sorting })),
      onColumnVisibilityChange: (visibility) => setState((prev) => ({ ...prev, columnVisibility: visibility })),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Effects
   useEffect(() => {
      dispatch(getCompanies());
   }, [dispatch]);

   useEffect(() => {
      const initialColumnVisibility = {};
      columnsReportsSalesDetail.forEach((column) => {
         initialColumnVisibility[column.id] = true;
      });
      setState((prev) => ({ ...prev, columnVisibility: initialColumnVisibility }));
   }, []);

   useEffect(() => {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId && companyList.length > 0) {
         const companyId = parseInt(storedCompanyId);
         const matchingCompany = companyList.find((comp) => comp.id === companyId);
         if (matchingCompany) {
            setState((prev) => ({ ...prev, companyFilter: [matchingCompany.id] }));
         }
      }
   }, [companyList]);

   // Data fetching
   const fetchData = useCallback(
      (searchTerm = state.searchTerm) => {
         if (refs.abortController.current) {
            refs.abortController.current.abort();
         }

         // Prevent duplicate calls with same parameters
         const currentParams = JSON.stringify({
            companyIds: companyIds.join(','),
            type: state.reportType,
            fromDate: dateRange.fromDate,
            toDate: dateRange.toDate,
            search: searchTerm.trim(),
         });

         if (lastFetchParamsRef.current === currentParams) {
            return; // Skip if same parameters
         }

         lastFetchParamsRef.current = currentParams;

         refs.abortController.current = new AbortController();
         setState((prev) => ({ ...prev, isLoading: true }));

         try {
            if (dateRange.fromDate && dateRange.toDate && companyIds.length > 0) {
               dispatch(
                  getReportPurchaseDetail({
                     companyIds: companyIds.join(','),
                     type: state.reportType,
                     isReturn: 1,
                     fromDate: dateRange.fromDate,
                     toDate: dateRange.toDate,
                     search: searchTerm.trim(),
                  }),
                  { signal: refs.abortController.current.signal },
               );
            }
         } catch (error) {
            if (error.name !== 'AbortError') {
               console.error('Error fetching purchase details:', error);
            }
         } finally {
            setState((prev) => ({ ...prev, isLoading: false }));
            refs.abortController.current = null;
         }
      },
      [
         state.searchTerm,
         state.reportType,
         refs.abortController,
         companyIds,
         dateRange.fromDate,
         dateRange.toDate,
         dispatch,
      ],
   );

   useEffect(() => {
      // Skip initial mount if no company filter set yet
      if (!initialLoadRef.current && companyIds.length === 0) {
         return;
      }

      // Clear any existing timeout
      if (refs.searchTimeout.current) {
         clearTimeout(refs.searchTimeout.current);
      }

      // Set up debouncing for search, immediate for other changes
      const delay = state.searchTerm ? SEARCH_DEBOUNCE_DELAY : 0;

      refs.searchTimeout.current = setTimeout(() => {
         fetchData();
      }, delay);

      // Mark as loaded after first successful setup
      if (!initialLoadRef.current) {
         initialLoadRef.current = true;
      }

      return () => {
         if (refs.searchTimeout.current) {
            clearTimeout(refs.searchTimeout.current);
         }
      };
   }, [
      companyIds,
      state.reportType,
      dateRange.fromDate,
      dateRange.toDate,
      state.searchTerm,
      fetchData,
      refs.searchTimeout,
   ]);

   const handleManualRefresh = useCallback(() => {
      fetchData();
   }, [fetchData]);

   // Event handlers
   const handleCompanyChange = useCallback((selectedOptions) => {
      setState((prev) => ({ ...prev, companyFilter: selectedOptions ? selectedOptions : [] }));
   }, []);

   const handleDateRangeChange = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
      setState((prev) => ({ ...prev, dateRange: { fromDate: from, toDate: to } }));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const toggleExpand = useCallback((id) => {
      setState((prev) => ({
         ...prev,
         expandedItems: { ...prev.expandedItems, [id]: !prev.expandedItems[id] },
      }));
   }, []);

   const handleSearchChange = useCallback(
      (e) => {
         const value = e.target.value;
         setState((prev) => ({ ...prev, searchTerm: value }));

         if (refs.searchTimeout.current) {
            clearTimeout(refs.searchTimeout.current);
         }

         refs.searchTimeout.current = setTimeout(() => {
            fetchData(value);
         }, SEARCH_DEBOUNCE_DELAY);
      },
      [fetchData],
   );

   const handleNextPage = () => {
      console.log('Next');
   };
   const handlePrevPage = () => {
      console.log('Prev');
   };

   useOutsideClick({
      ref: refs.wrapper,
      handler: () => {
         if (state.searchTerm === '') setState((prev) => ({ ...prev, expanded: false }));
      },
   });

   const handleDownloadExcel = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);

      // Formatting helpers
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `₹${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         datePartyWiseSalesRange.fromDate && datePartyWiseSalesRange.toDate
            ? `From ${formatDate(datePartyWiseSalesRange.fromDate)} to ${formatDate(datePartyWiseSalesRange.toDate)}`
            : 'All Dates';

      XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A1' });
      XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A2' });

      // Style title and date range
      ws['A1'].s = {
         font: { sz: 16, bold: true, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };
      ws['A2'].s = {
         font: { sz: 12, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };

      // Get visible columns
      const visibleColumns = columnsReportsSalesDetail.filter((col) => state.columnVisibility[col.id] !== false);
      const columnIds = visibleColumns.map((col) => col.id);

      // Start data from row 4
      let rowIndex = 3;

      if (state.reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData) => {
            // Company header
            XLSX.utils.sheet_add_aoa(ws, [[companyData.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge company header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Calculate company totals
            const companyTotals = companyData.data.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Add company summary
            const summaryRow = [
               'Company Summary',
               `Invoices: ${companyTotals.invoices}`,
               `Qty 1: ${formatNumber(companyTotals.qty1)}`,
               `Qty 2: ${formatNumber(companyTotals.qty2)}`,
               `Qty 3: ${formatNumber(companyTotals.qty3)}`,
               `Net Amount: ${formatCurrency(companyTotals.netAmount)}`,
            ];
            XLSX.utils.sheet_add_aoa(ws, [summaryRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style summary row
            summaryRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
               };
            });
            rowIndex++;

            // Add account data
            companyData.data.forEach((accountData) => {
               // Account summary row
               const accountRow = [
                  accountData.accountName,
                  accountData.data?.length || 0,
                  formatNumber(accountData.totalQty1),
                  formatNumber(accountData.totalQty2),
                  formatNumber(accountData.totalQty3),
                  formatCurrency(accountData.totalNetAmt),
               ];
               XLSX.utils.sheet_add_aoa(ws, [accountRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

               // Style account row
               accountRow.forEach((_, colIndex) => {
                  ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                     font: { color: { rgb: '000000' } },
                     fill: { fgColor: { rgb: colIndex === 0 ? 'E6E6E6' : 'F5F5F5' } },
                     alignment: { horizontal: colIndex === 0 ? 'left' : 'right' },
                  };
               });
               rowIndex++;
            });

            // Add space between companies
            rowIndex += 2;
         });
      } else {
         // Date-wise report format
         const headers = visibleColumns.map((col) => col.header);
         XLSX.utils.sheet_add_aoa(ws, [headers], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

         // Style headers
         headers.forEach((_, colIndex) => {
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
               font: { bold: true, color: { rgb: 'FFFFFF' } },
               fill: { fgColor: { rgb: '4F81BD' } },
               alignment: { horizontal: 'center' },
            };
         });
         rowIndex++;

         // Add transaction data
         salesReport.forEach((dateItem) => {
            // Date header
            XLSX.utils.sheet_add_aoa(ws, [[dateItem.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge date header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Add transactions for this date
            dateItem.data?.forEach((item) => {
               item.data?.forEach((transaction) => {
                  const transactionRow = columnIds.map((colId) => {
                     const value = transaction[colId];
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(value);
                     } else if (colId === 'date') {
                        return formatDate(value);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return value?.toString() || '';
                  });

                  XLSX.utils.sheet_add_aoa(ws, [transactionRow], {
                     origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
                  });

                  // Style transaction row
                  transactionRow.forEach((_, colIndex) => {
                     ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                        font: { color: { rgb: '000000' } },
                        fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'F5F5F5' } },
                        alignment: {
                           horizontal: [
                              'netAmount',
                              'basicAmount',
                              'taxableAmount',
                              'gstAmount',
                              'qty1',
                              'qty2',
                              'qty3',
                           ].includes(columnIds[colIndex])
                              ? 'right'
                              : 'left',
                        },
                     };
                  });
                  rowIndex++;
               });
            });

            // Add totals row for this date
            const totalsRow = columnIds.map((colId, colIndex) => {
               if (colIndex === 0) return 'Total';
               if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatNumber(total);
               }
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatCurrency(total);
               }
               return '';
            });

            XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style totals row
            totalsRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
                  fill: { fgColor: { rgb: 'E6E6E6' } },
                  alignment: {
                     horizontal: [
                        'netAmount',
                        'basicAmount',
                        'taxableAmount',
                        'gstAmount',
                        'qty1',
                        'qty2',
                        'qty3',
                     ].includes(columnIds[colIndex])
                        ? 'right'
                        : 'left',
                  },
               };
            });
            rowIndex += 2;
         });
      }

      // Set column widths
      ws['!cols'] = visibleColumns.map((col) => {
         if (col.id === 'accountName' || col.id === 'itemName') return { wch: 40 };
         if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(col.id)) return { wch: 20 };
         if (['qty1', 'qty2', 'qty3'].includes(col.id)) return { wch: 15 };
         return { wch: 25 };
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Summary');

      // Generate file name
      const dateRangeStr =
         datePartyWiseSalesRange.fromDate && datePartyWiseSalesRange.toDate
            ? `${new Date(datePartyWiseSalesRange.fromDate)
                 .toLocaleDateString('en-IN')
                 .replace(/\//g, '-')}_to_${new Date(datePartyWiseSalesRange.toDate)
                 .toLocaleDateString('en-IN')
                 .replace(/\//g, '-')}`
            : 'All_Dates';

      // Save the Excel file
      XLSX.writeFile(wb, `Sales_Summary_${dateRangeStr}.xlsx`);
   }, [salesReport, datePartyWiseSalesRange, state.reportType, state.columnVisibility, state.expandedItems]);

   const handleDownloadPDF = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const doc = new jsPDF({
         orientation: 'landscape',
         unit: 'mm',
      });

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         datePartyWiseSalesRange.fromDate && datePartyWiseSalesRange.toDate
            ? `From ${new Date(datePartyWiseSalesRange.fromDate).toLocaleDateString('en-IN')} to ${new Date(
                 datePartyWiseSalesRange.toDate,
              ).toLocaleDateString('en-IN')}`
            : 'All Dates';

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 10);
      doc.setFontSize(10);
      doc.text(dateRange, 14, 16);

      // Helper functions
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Start Y position for tables
      let startY = 25;

      if (state.reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData, companyIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Calculate company totals
            const companyTotals = companyData.data?.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(companyData.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Company Summary
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Invoices: ${companyTotals.invoices}`, 14, startY);
            doc.text(`Qty 1: ${formatNumber(companyTotals.qty1)}`, 50, startY);
            doc.text(`Qty 2: ${formatNumber(companyTotals.qty2)}`, 80, startY);
            doc.text(`Qty 3: ${formatNumber(companyTotals.qty3)}`, 110, startY);
            doc.text(`Net Amount: ${formatCurrency(companyTotals.netAmount)}`, 140, startY);
            startY += 8;

            // Account Summary Table
            if (companyData.data?.length) {
               const accountHeaders = ['Party Name', 'Invoices', 'Qty 1', 'Qty 2', 'Qty 3', 'Net Amount'];

               const accountRows = companyData.data.map((account) => [
                  account.accountName || 'Unnamed Account',
                  (account.data?.length || 0).toString(),
                  formatNumber(account.totalQty1),
                  formatNumber(account.totalQty2),
                  formatNumber(account.totalQty3),
                  formatCurrency(account.totalNetAmt),
               ]);

               autoTable(doc, {
                  startY,
                  head: [accountHeaders],
                  body: accountRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: {
                     0: { cellWidth: 60 },
                     1: { halign: 'right', cellWidth: 20 },
                     2: { halign: 'right', cellWidth: 20 },
                     3: { halign: 'right', cellWidth: 20 },
                     4: { halign: 'right', cellWidth: 20 },
                     5: { halign: 'right', cellWidth: 30 },
                  },
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between companies
            if (companyIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      } else {
         // Date-wise report format
         const visibleColumns = columnsReportsSalesDetail.filter((col) => state.columnVisibility[col.id] !== false);
         const columnIds = visibleColumns.map((col) => col.id);

         salesReport.forEach((dateItem, dateIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Date/Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(dateItem.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Prepare transaction data
            const transactions = dateItem.data?.flatMap((item) => item.data || []) || [];

            if (transactions.length > 0) {
               const headers = visibleColumns.map((col) => col.header);

               const transactionRows = transactions.map((transaction) => {
                  return columnIds.map((colId) => {
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(transaction[colId]);
                     } else if (colId === 'date') {
                        return formatDate(transaction[colId]);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return transaction[colId]?.toString() || '';
                  });
               });

               autoTable(doc, {
                  startY,
                  head: [headers],
                  body: transactionRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;

               // Add totals row
               const totalsRow = columnIds.map((colId, idx) => {
                  if (idx === 0) return 'Total';
                  if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatNumber(total);
                  }
                  if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatCurrency(total);
                  }
                  return '';
               });

               autoTable(doc, {
                  startY,
                  body: [totalsRow],
                  styles: {
                     fontStyle: 'bold',
                     fontSize: 9,
                     fillColor: [230, 230, 230],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between date items
            if (dateIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      }

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
         doc.setPage(i);
         doc.setFontSize(10);
         doc.setFont('helvetica', 'normal');
         doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, {
            align: 'right',
         });
         doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.height - 10);
      }

      // Save the PDF
      const dateRangeStr =
         datePartyWiseSalesRange.fromDate && datePartyWiseSalesRange.toDate
            ? `${new Date(datePartyWiseSalesRange.fromDate)
                 .toLocaleDateString('en-IN')
                 .replace(/\//g, '-')}_to_${new Date(datePartyWiseSalesRange.toDate)
                 .toLocaleDateString('en-IN')
                 .replace(/\//g, '-')}`
            : 'All_Dates';

      doc.save(`Sales_Summary_${dateRangeStr}.pdf`);
   }, [salesReport, datePartyWiseSalesRange, state.reportType, state.columnVisibility, state.expandedItems]);

   const deletePurchaseReturn = () => {
      const selectedDaybook = daybookData.find((db) => db.id === viewDetail?.daybookId);
      console.log('viewDetail data', viewDetail, selectedDaybook);
      if (selectedDaybook && selectedDaybook?.askPasswordOnDelete) {
         setDataId({ id: viewDetail?.id, daybookId: viewDetail.daybookId, accountId: viewDetail.accountId });
         setIsModalOpen(true);
         setIsOpenPurchaseReturnSingleView(false);
      } else {
         deleteRecord(
            viewDetail?.id,
            deletePurchaseReturnInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
         setIsOpenPurchaseReturnSingleView(false);
      }
      // deleteRecord(
      //    viewDetail?.id,
      //    deletePurchaseInvoice,
      //    dispatch,
      //    user?.loginhistory?.companyId,
      //    userGlobalSettingsData,
      // );
      // setIsOpenPurchaseReturnSingleView(false);
   };

   const handleSalesDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView();
   };

   // Render functions
   const renderDataRows = useCallback(
      (itemData) => {
         if (!itemData || !Array.isArray(itemData)) return null;

         const handledata = async (data) => {
            const storedCompanyId = localStorage.getItem('companyId');
            const companyId = parseInt(storedCompanyId);
            if (data.companyId === companyId) {
               await dispatch(
                  getSinglePurchaseWithPrevAndNext({
                     id: data.id,
                     page: 1,
                     limit: 1,
                     sortBy: 'id',
                     sortOrder: 'desc',
                  }),
               );
               setIsOpenPurchaseReturnSingleView(true);
            }
         };

         return itemData.map((transaction, idx) => {
            const isOddRow = idx % 2 !== 0;

            return (
               <tr
                  key={`row-${transaction.id || idx}`}
                  className={`
                 cursor-pointer text-center text-sm font-medium
                 transition-all duration-200 ease-in-out
                 ${isDark ? 'text-gray-100' : 'text-gray-800'}
                 ${isOddRow ? (isDark ? 'bg-slate-800/40' : 'bg-blue-50/50') : 'bg-transparent'}
                 hover:bg-gradient-to-r
                 ${
                    isDark
                       ? 'hover:from-blue-900/40 hover:to-indigo-900/40'
                       : 'hover:from-blue-100/60 hover:to-indigo-100/60'
                 }
                 hover:shadow-sm
                 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200/70'}
               `}
                  onClick={() => handledata(transaction)}>
                  {table.getVisibleFlatColumns().map((column) => {
                     const columnId = column.id;
                     const cellValue = transaction[columnId];
                     const isNumeric = [
                        'netAmount',
                        'dueDays',
                        'returnadjamount',
                        'otheradjamount',
                        'balanceamount',
                        'recDate',
                        'recBook',
                     ].includes(columnId);

                     if (columnId === 'itemName' && transaction.saleLineItems) {
                        return (
                           <td
                              key={columnId}
                              className={`px-1 py-1 max-w-[150px] truncate ${
                                 isDark ? 'border-slate-700/50' : 'border-gray-200/70'
                              }`}>
                              {transaction.saleLineItems.map((item) => item.itemName).join(', ')}
                           </td>
                        );
                     }

                     if (columnId.includes('Date') && cellValue) {
                        return (
                           <td
                              key={columnId}
                              className={`px-1 py-1 max-w-[100px] ${
                                 isDark ? 'border-slate-700/50' : 'border-gray-200/70'
                              }`}>
                              {new Date(cellValue).toLocaleDateString()}
                           </td>
                        );
                     }

                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(columnId) &&
                        cellValue !== undefined
                     ) {
                        return (
                           <td
                              key={columnId}
                              className={`px-1 py-1 text-right max-w-[150px] font-semibold ${
                                 isDark ? 'text-emerald-400 border-slate-700/50' : 'text-emerald-700 border-gray-200/70'
                              }`}>
                              ₹{Number(cellValue).toLocaleString()}
                           </td>
                        );
                     }

                     return (
                        <td
                           key={columnId}
                           className={`px-1 py-1 max-w-[150px] ${isNumeric ? 'text-right' : ''} ${
                              isDark ? 'border-slate-700/50' : 'border-gray-200/70'
                           }`}>
                           {cellValue}
                        </td>
                     );
                  })}
               </tr>
            );
         });
      },
      [table, isDark],
   );

   const renderReportContent = useCallback(() => {
      const visibleColumns = table.getVisibleFlatColumns().length;

      if (state.isLoading) {
         return <Loader colSpan={visibleColumns} />;
      }

      if (!salesReport || salesReport.length === 0) {
         return <NoDataFound colSpan={visibleColumns} />;
      }

      if (state.reportType === REPORT_TYPE.party) {
         const PartyHeaderRow = () => (
            <tr
               className={`
               ${
                  isDark
                     ? 'bg-gradient-to-r from-slate-700 to-slate-600 text-gray-100'
                     : 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700'
               }
               border-b-2 ${isDark ? 'border-indigo-500/50' : 'border-indigo-400/50'}
             `}>
               <th className="w-2/5 px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Party Name</th>
               <th className="w-1/5 px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Items</th>
               <th className="w-1/5 px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Qty</th>
               <th className="w-1/5 px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Net Amount</th>
            </tr>
         );

         return salesReport.map((companyData, companyIndex) => {
            const companyKey = `company-${companyData.companyId || companyIndex}`;

            if (state.expandedItems[companyKey] === undefined) {
               setState((prev) => ({ ...prev, expandedItems: { ...prev.expandedItems, [companyKey]: true } }));
            }

            return (
               <React.Fragment key={companyKey}>
                  <tr
                     className={`
                   cursor-pointer transition-all duration-300
                   ${
                      isDark
                         ? 'bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 text-gray-100 border-l-4 border-l-indigo-500'
                         : 'bg-gradient-to-r from-indigo-100 via-blue-50 to-indigo-100 text-gray-800 border-l-4 border-l-indigo-600'
                   }
                   hover:shadow-lg hover:scale-[1.001]
                   border-b-2 ${isDark ? 'border-indigo-600/40' : 'border-indigo-300/60'}
                 `}
                     onClick={() => toggleExpand(companyKey)}>
                     <td className="px-3 py-3">
                        <span className="font-bold text-base tracking-wide">{companyData.companyName}</span>
                     </td>
                     <td className="px-3 py-3 text-right font-bold">
                        <span className={`${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                           Items: {companyData.data.reduce((sum, account) => sum + account.data.length, 0)}
                        </span>
                     </td>
                     <td className="px-3 py-3 text-right font-bold">
                        <span className={`${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                           Qty: {companyData.data.reduce((sum, account) => sum + (Number(account.totalQty1) || 0), 0)}
                        </span>
                     </td>
                     <td className="px-3 py-3 text-right font-bold">
                        <div className="flex items-center justify-end gap-3">
                           <span className={`${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                              Net Amount: ₹
                              {companyData.data
                                 .reduce((sum, account) => sum + (Number(account.totalNetAmt) || 0), 0)
                                 .toLocaleString()}
                           </span>
                           {state.expandedItems[companyKey] ? (
                              <ChevronUpIcon className="w-5 h-5" />
                           ) : (
                              <ChevronDownIcon className="w-5 h-5" />
                           )}
                        </div>
                     </td>
                  </tr>

                  {state.expandedItems[companyKey] && (
                     <>
                        <PartyHeaderRow />
                        {companyData.data?.map((accountData, accountIndex) => {
                           const accountKey = `account-${accountData.accountId || accountIndex}`;

                           if (state.expandedItems[accountKey] === undefined) {
                              setState((prev) => ({
                                 ...prev,
                                 expandedItems: { ...prev.expandedItems, [accountKey]: true },
                              }));
                           }

                           return (
                              <React.Fragment key={accountKey}>
                                 <tr
                                    className={`
                             cursor-pointer text-xs transition-all duration-200
                             ${
                                isDark
                                   ? 'bg-slate-800/60 text-gray-200 border-l-2 border-l-blue-500/60'
                                   : 'bg-white text-gray-700 border-l-2 border-l-blue-400/60'
                             }
                             hover:shadow-md
                             border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200/70'}
                           `}
                                    onClick={() => toggleExpand(accountKey)}>
                                    <td className="px-3 py-2 font-semibold">{accountData.accountName}</td>
                                    <td className="px-3 py-2 text-right">{accountData.data.length}</td>
                                    <td className="px-3 py-2 text-right">{accountData.totalQty1}</td>
                                    <td className="px-3 py-2 text-right font-semibold">
                                       <span className={`${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                          ₹{Number(accountData.totalNetAmt).toLocaleString()}
                                       </span>
                                    </td>
                                 </tr>
                              </React.Fragment>
                           );
                        })}
                     </>
                  )}
               </React.Fragment>
            );
         });
      }

      return salesReport.map((dateItem, index) => {
         const itemData = dateItem.data?.flatMap((item) => item.data || []) || [];

         return (
            <React.Fragment key={`date-${dateItem.date || index}`}>
               <tr
                  className={`
                 font-bold
                 ${
                    isDark
                       ? 'bg-gradient-to-r from-blue-900 to-indigo-900 text-gray-100 border-l-4 border-l-blue-500'
                       : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-gray-800 border-l-4 border-l-blue-600'
                 }
                 border-b-2 ${isDark ? 'border-blue-600/40' : 'border-blue-300/60'}
               `}>
                  <td
                     colSpan={15}
                     className="px-3 py-3 text-sm max-w-[150px] overflow-hidden whitespace-nowrap text-ellipsis">
                     <div className="flex justify-between items-center">
                        <span className="font-bold text-lg tracking-wide">{dateItem.companyName}</span>
                     </div>
                  </td>
               </tr>

               {state.reportType !== REPORT_TYPE.party &&
                  table.getHeaderGroups().map((headerGroup) => (
                     <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                           if (!header.column.getIsVisible()) return null;

                           const columnId = header.column.id;
                           const isNumeric = [
                              'netAmount',
                              'dueDays',
                              'returnadjamount',
                              'otheradjamount',
                              'balanceamount',
                              'recDate',
                              'recBook',
                           ].includes(columnId);

                           return (
                              <th
                                 key={header.id}
                                 colSpan={header.colSpan}
                                 className={`
                           cursor-pointer px-2 py-3 text-xs font-bold uppercase tracking-wider
                           whitespace-nowrap sticky top-0 z-10
                           ${
                              isDark
                                 ? 'bg-slate-800 text-gray-300 border-slate-700/50'
                                 : 'bg-gray-50 text-gray-700 border-gray-300/70'
                           }
                           ${isNumeric ? 'text-right' : 'text-left'}
                           border-b-2 ${isDark ? 'border-indigo-500/50' : 'border-indigo-400/50'}
                         `}
                                 onClick={header.column.getToggleSortingHandler()}>
                                 <div
                                    className={`flex items-center text-sm font-semibold ${
                                       isNumeric ? 'justify-end' : 'justify-between'
                                    }`}>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    <span className="ml-1">
                                       {{
                                          asc: '↑',
                                          desc: '↓',
                                       }[header.column.getIsSorted()] ?? null}
                                    </span>
                                 </div>
                              </th>
                           );
                        })}
                     </tr>
                  ))}

               {dateItem.data?.map((item, itemIndex) => (
                  <React.Fragment key={`item-${itemIndex}`}>
                     {Array.isArray(item.data) && renderDataRows(item.data)}
                  </React.Fragment>
               ))}

               {itemData.length > 0 && (
                  <tr
                     className={`
                   font-bold
                   ${
                      isDark
                         ? 'bg-gradient-to-r from-slate-800 to-slate-700 text-gray-100 border-t-2 border-t-emerald-500/50'
                         : 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-t-2 border-t-emerald-600/50'
                   }
                   shadow-inner
                 `}>
                     {table.getVisibleFlatColumns().map((column, colIndex, visibleCols) => {
                        if (column.id === visibleCols[0].id) {
                           return (
                              <td key={`grand-total-${column.id}`} className="px-3 py-3 font-bold text-base">
                                 Grand Total
                              </td>
                           );
                        }

                        if (column.id === 'qty' || column.id.toLowerCase().includes('qty')) {
                           const grandTotalQty = itemData.reduce((sum, t) => {
                              const val = Number(t[column.id]);
                              return sum + (isNaN(val) ? 0 : val);
                           }, 0);

                           return (
                              <td
                                 key={`grand-total-${column.id}`}
                                 className={`px-3 py-3 font-bold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                                 {grandTotalQty}
                              </td>
                           );
                        }

                        if (column.id === 'netAmount') {
                           const total = itemData.reduce((sum, t) => sum + (Number(t.netAmount) || 0), 0);
                           return (
                              <td
                                 key={`grand-total-${column.id}`}
                                 className={`px-3 py-3 text-right font-bold text-base ${
                                    isDark ? 'text-emerald-300' : 'text-emerald-700'
                                 }`}>
                                 ₹{total.toLocaleString()}
                              </td>
                           );
                        }

                        if (['basicAmount', 'taxableAmount', 'gstAmount'].includes(column.id)) {
                           const total = itemData.reduce((sum, t) => sum + (Number(t[column.id]) || 0), 0);
                           return (
                              <td
                                 key={`grand-total-${column.id}`}
                                 className={`px-3 py-3 text-right font-bold ${
                                    isDark ? 'text-emerald-300' : 'text-emerald-700'
                                 }`}>
                                 ₹{total.toLocaleString()}
                              </td>
                           );
                        }

                        return <td key={`grand-total-${column.id}`} className="px-3 py-3"></td>;
                     })}
                  </tr>
               )}
            </React.Fragment>
         );
      });
   }, [
      table,
      state.isLoading,
      state.reportType,
      state.expandedItems,
      salesReport,
      isDark,
      toggleExpand,
      renderDataRows,
   ]);

   const renderHeader = () => (
      <Flex pr="25px" mb="8px" justifyContent="space-between" align="center" mt="50px" overflow="hidden">
         <Flex>
            <Image
               src={back}
               alt="Go Back"
               width="15px"
               onClick={() => window.history.back()}
               style={{ cursor: 'pointer' }}
            />
            <Text color={theme.textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
               Purchase Return Summary
            </Text>
         </Flex>
      </Flex>
   );

   const renderFilters = () => (
      <Box mt="15px" bg={theme.backgroundColor} px={4} py={0} borderRadius="7px">
         <Flex alignItems="center" gap={3} justifyContent="end">
            <Box my={2} width="300px" fontSize="13px" fontWeight={500} display={{ base: 'none', xl: 'block' }}>
               <GridItem className="seperator-width" mt={5}>
                  {renderDynamicDropdown(
                     'company',
                     'Company',
                     lastcompanyOptions,
                     { value: state.companyFilter, onChange: handleCompanyChange },
                     {},
                     theme.textColor,
                     false,
                     'Select company',
                     true,
                     undefined,
                     true,
                  )}
               </GridItem>
            </Box>

            <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
               <Flex alignItems="center" mt={3}>
                  <PeriodDatePicker
                     value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                     onChange={handleDateRangeChange}
                     defaultPeriod="last7Days"
                     style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                  />
               </Flex>

               <Box my={2} zIndex={15} fontSize={'13px'} fontWeight={500}>
                  <label>Report Type</label>
                  <Select
                     options={REPORT_TYPE_OPTIONS}
                     value={REPORT_TYPE_OPTIONS.find((option) => option.value === state.reportType)}
                     onChange={(selectedOption) => {
                        setState((prev) => ({
                           ...prev,
                           reportType: selectedOption.value,
                           expandedItems: {},
                        }));
                     }}
                     placeholder="Select Report Type"
                     styles={{
                        control: (baseStyles, state) => ({
                           ...baseStyles,
                           minHeight: '30px',
                           height: '30px',
                           backgroundColor: controlBg,
                           borderColor: controlBorder,
                           boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                           '&:hover': {
                              borderColor: controlBorderHover,
                           },
                        }),
                        valueContainer: (baseStyles) => ({
                           ...baseStyles,
                           height: '30px',
                           padding: '0 6px',
                           color: theme.textColor,
                        }),
                        indicatorsContainer: (baseStyles) => ({
                           ...baseStyles,
                           height: '30px',
                        }),
                        singleValue: (baseStyles) => ({
                           ...baseStyles,
                           color: theme.textColor,
                        }),
                        placeholder: (baseStyles) => ({
                           ...baseStyles,
                           color: placeholderColor,
                        }),
                        option: (baseStyles, state) => ({
                           ...baseStyles,
                           backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                           color: theme.textColor,
                           '&:hover': {
                              backgroundColor: optionHoverBg,
                           },
                        }),
                        menu: (baseStyles) => ({
                           ...baseStyles,
                           backgroundColor: menuBg,
                           border: `1px solid ${menuBorder}`,
                        }),
                        dropdownIndicator: (baseStyles) => ({
                           ...baseStyles,
                           color: indicatorColor,
                           '&:hover': {
                              color: indicatorHoverColor,
                           },
                        }),
                        clearIndicator: (baseStyles) => ({
                           ...baseStyles,
                           color: indicatorColor,
                           '&:hover': {
                              color: indicatorHoverColor,
                           },
                        }),
                     }}
                  />
               </Box>
            </Box>

            <Flex alignItems="center">
               <Box flex="1" mr={3}>
                  <MotionFlex
                     ref={refs.wrapper}
                     align="center"
                     bg={theme.bg}
                     borderRadius="full"
                     cursor="pointer"
                     px={state.expanded ? 3 : 0}
                     w={state.expanded ? '250px' : '40px'}
                     h="40px"
                     transition="all 0.3s ease"
                     onClick={() => setState((prev) => ({ ...prev, expanded: true }))}>
                     {state.isLoading ? (
                        <Spinner size="sm" color={theme.icon} />
                     ) : (
                        <MotionSearchIcon
                           color={theme.icon}
                           animate={{ rotate: state.expanded ? 0 : 90 }}
                           transition={{ duration: 0.3 }}
                           style={{
                              marginRight: state.expanded ? '8px' : '10px',
                              marginLeft: state.expanded ? '0' : '10px',
                           }}
                        />
                     )}
                     {state.expanded && (
                        <InputGroup width="200px">
                           <Input
                              variant="unstyled"
                              placeholder="Search ..."
                              value={state.searchTerm}
                              onChange={handleSearchChange}
                              autoFocus
                              color={theme.icon}
                              flex="1"
                           />
                        </InputGroup>
                     )}
                  </MotionFlex>
               </Box>

               <Button onClick={handleManualRefresh} _hover={{ bg: 'blue.50' }} mr={2}>
                  <LuRefreshCcw size={20} mr={2} />
               </Button>

               <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                  <IoFilterOutline size={22} />
               </Button>

               <Tooltip label="Show/Hide Columns">
                  <IconButton
                     icon={<ViewIcon />}
                     onClick={() => setState((prev) => ({ ...prev, isColumnSelectorOpen: true }))}
                     aria-label="Column Visibility"
                  />
               </Tooltip>

               <Flex mr={2}>
                  <Popover>
                     <PopoverTrigger>
                        <IconButton icon={<Image src={Download} boxSize="24px" />} aria-label="Download Options" />
                     </PopoverTrigger>
                     <PopoverContent width="220px" zIndex="10" bg={theme.backgroundColor} mr={8}>
                        <PopoverBody p="">
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={handleDownloadExcel}
                              justifyContent="space-between"
                              mb={3}>
                              <img src={xls} alt="Download XLS" width="30px" />
                              Download Excel
                           </Button>
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={handleDownloadPDF}
                              justifyContent="space-between"
                              mb={3}>
                              <img src={xls} alt="Download PDF" width="30px" />
                              Download Pdf
                           </Button>
                        </PopoverBody>
                     </PopoverContent>
                  </Popover>
               </Flex>
            </Flex>

            <Box ml={1}>
               <Flex display={{ base: 'block', xl: 'none' }}>
                  <Button variant="unstyled" onClick={onOpen}>
                     <Image src={DotIcon} alt="Menu" width="25px" />
                  </Button>
               </Flex>
            </Box>
         </Flex>
      </Box>
   );

   const renderTable = () => (
      <Grid overflow="auto">
         <Card mt="15px" borderRadius="7px">
            <Box
               overflowX="auto"
               className="custom-scrollbar"
               position="relative"
               minH={{ base: '60vh', md: '65vh', xl: '70vh' }}
               maxH={{ base: '60vh', md: '65vh', xl: '70vh' }}>
               <Table variant="simple" color={theme.textColor} mb="24px" mt="12px">
                  <Thead>
                     {/* {state.reportType !== REPORT_TYPE.party &&
                        table.getHeaderGroups().map((headerGroup) => (
                           <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => {
                                 if (!header.column.getIsVisible()) return null;

                                 const columnId = header.column.id;
                                 const isNumeric = [
                                    'netAmount',
                                    'dueDays',
                                    'returnadjamount',
                                    'otheradjamount',
                                    'balanceamount',
                                    'recDate',
                                    'recBook',
                                 ].includes(columnId);

                                 return (
                                    <Th
                                       key={header.id}
                                       colSpan={header.colSpan}
                                       borderColor={theme.borderColor}
                                       cursor="pointer"
                                       p="2px"
                                       py="4px"
                                       fontSize="10px"
                                       fontWeight="600"
                                       bg="#F5F5F5"
                                       position="sticky"
                                       top="-1"
                                       zIndex="1"
                                       _dark={{ bg: '#080238' }}
                                       whiteSpace="nowrap"
                                       color={theme.textColor}
                                       isNumeric={isNumeric}
                                       onClick={header.column.getToggleSortingHandler()}>
                                       <Flex
                                          justifyContent={isNumeric ? 'flex-end' : 'space-between'}
                                          align="center"
                                          fontSize={{ sm: '12px', lg: '14px' }}
                                          fontWeight="">
                                          {flexRender(header.column.columnDef.header, header.getContext())}
                                          {{
                                             asc: '↑',
                                             desc: '↓',
                                          }[header.column.getIsSorted()] ?? null}
                                       </Flex>
                                    </Th>
                                 );
                              })}
                           </Tr>
                        ))} */}
                  </Thead>
                  <Tbody>{renderReportContent()}</Tbody>
               </Table>
            </Box>
         </Card>
      </Grid>
   );

   const renderMobileDrawer = () => (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} finalFocusRef={refs.fromDateInput}>
         <DrawerOverlay />
         <DrawerContent borderTopRadius="20px">
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
            <DrawerBody>
               <GridItem className="seperator-width">
                  {renderDynamicDropdown(
                     'company',
                     'Company',
                     lastcompanyOptions,
                     { value: state.companyFilter, onChange: handleCompanyChange },
                     {},
                     theme.textColor,
                     false,
                     'Select company',
                     true,
                     undefined,
                     true,
                  )}
               </GridItem>
               <Box my={2} fontSize="13px" fontWeight={500}>
                  <label>Report Type</label>
                  <Select
                     options={REPORT_TYPE_OPTIONS}
                     value={REPORT_TYPE_OPTIONS.find((option) => option.value === state.reportType)}
                     onChange={(selectedOption) => {
                        setState((prev) => ({
                           ...prev,
                           reportType: selectedOption.value,
                           expandedItems: {},
                        }));
                     }}
                     placeholder="Select Report Type"
                  />
               </Box>
               <Box alignItems="center" mt={3}>
                  <PeriodDatePicker
                     value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                     onChange={handleDateRangeChange}
                     defaultPeriod="last7Days"
                     style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                  />
               </Box>

               <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                  Apply Filters
               </Button>
            </DrawerBody>
         </DrawerContent>
      </Drawer>
   );

   return (
      <>
         {renderHeader()}
         {renderFilters()}
         {renderTable()}
         {renderMobileDrawer()}
         <ColumnVisibilityModal
            isOpen={state.isColumnSelectorOpen}
            onClose={() => setState((prev) => ({ ...prev, isColumnSelectorOpen: false }))}
            columns={columnsReportsSalesDetail}
            columnVisibility={state.columnVisibility}
            setColumnVisibility={(visibility) => setState((prev) => ({ ...prev, columnVisibility: visibility }))}
         />

         <SalesPurchaseFilter
            type={state.reportType}
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            companyIds={companyIds}
            fromDate={datePartyWiseSalesRange.fromDate}
            toDate={datePartyWiseSalesRange.toDate}
            entityType="purchaseDetail"
         />

         <SalesDetails
            isOpen={isOpenPurchaseReturnSingleView}
            onClose={handleSalesDetailsClose}
            singleEntityRights={purchaseInvoiceRight || purchaseReturnRight}
            title={'Purchase Return'}
            entityType={'purchasereturn'}
            singleEntity={viewDetail}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            setIsEditFormOpen={setIsEditFormOpen}
            setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
            isEditFormOpen={isEditFormOpen}
            // paginate={paginateData}
            viewDetail={viewDetail}
            lineItems={viewDetail?.lineitems}
            billTerms={viewDetail?.purchasereturnbillteams}
            onDelete={deletePurchaseReturn}
            handleSerchClose={() => console.log('')}
         />

         <DeletePasswordModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deletePurchaseReturn}
            id={dataId}
            deleteApi={deletePurchaseReturnInvoice}
            dispatch={dispatch}
            type={'receive'}
            userGlobalSettingsData={userGlobalSettingsData}
         />
      </>
   );
};

export default PurchaseReturnSummary;
